import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Mail, Phone, Flame } from "lucide-react";

/**
 * NowPage - The "return to focus" surface
 * 
 * Design philosophy:
 * - This is NOT a dashboard
 * - This is a re-entry moment
 * - "Quiet clarity. Not overbuilt organization."
 * 
 * Feel: "Unlocking your phone and remembering why you picked it up"
 */

const LAST_ACTIVE_KEY = 'wwi_last_active_task';
const STORAGE_KEY = 'wherewasi_data';
const FIRE_COUNTER_KEY = 'wwi_fire_counter';

// Category icon component - subtle, not dominant
const CategoryIcon = ({ category }) => {
  const iconProps = { 
    size: 14, 
    strokeWidth: 1.5,
    className: "text-stone-400"
  };
  
  switch (category) {
    case 'email':
      return <Mail {...iconProps} />;
    case 'phone':
      return <Phone {...iconProps} />;
    default:
      return <Zap {...iconProps} />;
  }
};

// Validate if task still exists in stored board data
const validateTaskExists = (taskId) => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return false;
    
    const data = JSON.parse(stored);
    if (!data?.boards?.[0]) return false;
    
    for (const list of data.boards[0].lists) {
      if (list.cards.some(card => card.id === taskId)) {
        return true;
      }
    }
    return false;
  } catch {
    return false;
  }
};

// Get flame progress from localStorage
const getFlameProgress = () => {
  try {
    const stored = localStorage.getItem(FIRE_COUNTER_KEY);
    if (!stored) return null;
    const data = JSON.parse(stored);
    return {
      current: data.count || 0,
      // Show progress toward next milestone (25, 50, 100)
      nextMilestone: data.count < 25 ? 25 : data.count < 50 ? 50 : 100
    };
  } catch {
    return null;
  }
};

const NowPage = () => {
  const navigate = useNavigate();
  const [lastTask, setLastTask] = useState(null);
  const [flameProgress, setFlameProgress] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Load last active task from localStorage (instant, <10ms)
    try {
      const stored = localStorage.getItem(LAST_ACTIVE_KEY);
      if (stored) {
        const task = JSON.parse(stored);
        // Validate task still exists
        if (task?.taskId && validateTaskExists(task.taskId)) {
          setLastTask(task);
        }
      }
      // Load flame progress (optional secondary context)
      const flame = getFlameProgress();
      if (flame && flame.current > 0) {
        setFlameProgress(flame);
      }
    } catch (err) {
      // Invalid/corrupt localStorage data - safe to ignore, page will show empty state
      console.debug('NowPage: failed to read last-active task from localStorage', err);
    }
    setIsLoading(false);
  }, []);

  // Handle navigation to full board with focus
  const handleResume = () => {
    if (lastTask?.taskId) {
      navigate(`/app?focus=${lastTask.taskId}`);
    } else {
      navigate('/app');
    }
  };

  // Minimal loading - just a breath
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]" />
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center justify-center px-6">
      {/* Main content container */}
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
        className="w-full max-w-sm"
      >
        {lastTask ? (
          /* Has last active task - show return to focus */
          <>
            {/* 1. Header - very light presence */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.4 }}
              className="text-center text-stone-400 text-sm font-normal mb-8 tracking-wide"
            >
              You were working on…
            </motion.p>

            {/* 2. Primary Task Card - centerpiece */}
            <motion.button
              initial={{ opacity: 0, y: 3 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.45, ease: [0.25, 0.1, 0.25, 1] }}
              onClick={handleResume}
              className="w-full text-left bg-white rounded-2xl p-5 mb-4 active:scale-[0.98] transition-transform relative overflow-hidden"
              style={{
                // Elevated shadow
                boxShadow: '0 6px 24px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.04)',
              }}
              data-testid="now-task-card"
            >
              {/* Soft highlight edge - very light teal glow */}
              <div 
                className="absolute inset-0 rounded-2xl pointer-events-none"
                style={{
                  boxShadow: 'inset 0 0 0 1px rgba(61, 196, 208, 0.12)',
                }}
              />
              
              {/* Category icon - optional, subtle */}
              <div className="flex items-center gap-2 mb-3">
                <CategoryIcon category={lastTask.category} />
                <span className="text-[11px] text-stone-400 uppercase tracking-wider font-medium">
                  {lastTask.category === 'email' ? 'Email' : lastTask.category === 'phone' ? 'Call' : 'Action'}
                </span>
              </div>
              
              {/* Task text - clear, readable, slightly larger */}
              <p className="text-[18px] leading-relaxed text-stone-800 font-normal">
                {lastTask.content}
              </p>
            </motion.button>

            {/* 3. Resume Affordance - micro text */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              className="text-center text-stone-300 text-xs mb-10"
            >
              Tap to continue
            </motion.p>

            {/* 4. Secondary Context - flame progress (optional, subtle) */}
            {flameProgress && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.4 }}
                className="flex items-center justify-center gap-1.5 text-stone-400"
              >
                <Flame size={14} className="text-orange-400/70" />
                <span className="text-xs font-medium">
                  {flameProgress.current}
                  <span className="text-stone-300 font-normal"> / {flameProgress.nextMilestone}</span>
                </span>
              </motion.div>
            )}
          </>
        ) : (
          /* 5. Empty State - nothing in progress */
          <>
            <motion.div
              initial={{ opacity: 0, y: 3 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.45, ease: [0.25, 0.1, 0.25, 1] }}
              className="text-center mb-10"
            >
              <p className="text-stone-500 text-lg leading-relaxed mb-1">
                Nothing in progress.
              </p>
              <p className="text-stone-400 text-base">
                Start something small.
              </p>
            </motion.div>

            {/* Subtle CTA - go to board */}
            <motion.div
              initial={{ opacity: 0, y: 3 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="text-center"
            >
              <Link
                to="/app"
                data-testid="now-go-to-board"
                className="inline-flex items-center text-sm text-stone-500 hover:text-[#F58227] transition-colors"
              >
                Go to board
              </Link>
            </motion.div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default NowPage;
